import React, { useMemo } from 'react'

import { Box } from 'ui/components/Box'
import { Select, SelectOption } from 'ui/components/Select'
import { Body } from 'ui/components/Text'

import { AttachmentDefaultValue } from './AttachmentDefaultValue'
import { CheckboxDefaultValue } from './CheckboxDefaultValue'
import { DateAndDateTimeDefaultValue } from './DateAndDateTimeDefaultValue'
import { LongTextDefaultValue } from './LongTextDefaultValue'
import { NumericDefaultValue } from './NumericDefaultValue'
import { PercentageDefaultValue } from './PercentageDefaultValue'
import { RelationshipFieldDefaultValue } from './RelationshipFieldDefaultValue'
import { RichTextDefaultValue } from './RichTextDefaultValue'
import { SelectDefaultValue } from './SelectDefaultValue'
import { StringDefaultValue } from './StringDefaultValue'
import { useUserFieldOptionsForDefaultValues } from './UseUserFieldOptionsForDefaultValues'

type DefaultValueWrapperProps = {
    value: FieldDto['default_value']
    onChange: (value: FieldDto['default_value']) => void
    field: FieldDto
}
export const DefaultValueWrapper: React.FC<DefaultValueWrapperProps> = ({
    field,
    value,
    onChange,
}) => {
    const userFieldOptions = useUserFieldOptionsForDefaultValues(field)
    // if the field type is being modified, selectedType is set - we want to show the default config for the new field
    // type, even before saved
    const effectiveFieldType = (field as any).selectedType ?? field.type

    const constantInputComponent = useMemo(() => {
        switch (effectiveFieldType) {
            case 'lookup':
            case 'multi_lookup':
                return (
                    <RelationshipFieldDefaultValue
                        value={value}
                        onChange={onChange}
                        field={field}
                    />
                )
            case 'string':
            case 'url':
                return <StringDefaultValue value={value} onChange={onChange} />
            case 'long_text':
                return <LongTextDefaultValue value={value} onChange={onChange} />

            case 'document':
                return <RichTextDefaultValue value={value} onChange={onChange} />
            case 'number':
            case 'currency':
                return <NumericDefaultValue value={value} onChange={onChange} />
            case 'percentage':
                return <PercentageDefaultValue value={value} onChange={onChange} />

            case 'checkbox':
                return <CheckboxDefaultValue value={value} onChange={onChange} />
            case 'dropdown':
            case 'multi_select':
                return <SelectDefaultValue value={value} onChange={onChange} field={field} />
            case 'date':
            case 'datetime':
                return (
                    <DateAndDateTimeDefaultValue value={value} onChange={onChange} field={field} />
                )
            case 'multi_file':
                return <AttachmentDefaultValue value={value} onChange={onChange} />
            default:
                return null
        }
    }, [effectiveFieldType, field, value, onChange])

    return (
        <Box>
            <Select
                placeholder="Select a default value type (optional)"
                value={value?.type}
                onChange={
                    (value: any) => onChange({ type: value, value: undefined }) // clear existing value when switching
                }
                isClearable
                modal={false}
            >
                <SelectOption value="constant" label="Fixed value" color="Fuchsia" />
                <SelectOption value="user" label="The user's..." color="Orange" />
            </Select>

            <Box mt="m">
                {value?.type === 'constant' ? (
                    constantInputComponent
                ) : value?.type === 'user' ? (
                    <Select
                        placeholder="Select user field"
                        value={value?.value}
                        onChange={(value: any) => onChange({ type: 'user', value })}
                        isClearable
                        modal={false}
                    >
                        {userFieldOptions.map((opt) => (
                            <SelectOption
                                key={opt.value}
                                value={opt.value}
                                label={`The user's 👤 ${opt.label}`}
                            />
                        ))}

                        {userFieldOptions?.length === 0 && (
                            <Box flex justifyContent="center">
                                <Body size="xs">No options available</Body>
                            </Box>
                        )}
                    </Select>
                ) : null}
            </Box>
        </Box>
    )
}
